// @src/pre-deployment/src/components/TranslationMatching.js v2.4 - IMPORT ORDER FIXED
// - CRITICAL FIX: Remove conflicting imports that caused white screen
// - FIXED: Import/export conflict between useVocabulary and useDataService
// - FIXED: Import order violations (moved all imports to top)
// - Updated for ChapterContainer compatibility

import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { VocabularyTracking, ChapterProgress } from '../../data/tracking';
import { useVocabulary, useSessionProgressActions } from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';

console.log('🔄 TranslationMatching.js FILE LOADED'); // Diagnostic: File loading

// Add safe logging helper to prevent console crashes
const safeLog = (group, message, data) => {
    try {
        if (group) console.group(group);
        console.log(message, data || '');
        if (group) console.groupEnd();
    } catch (_e) {
        // Silent fail on logging errors
    }
};

// Local fallback vocabulary (won't break if Firebase fails)
const fallbackVocabularyData = [
    { korean: '병원', translations: { english: 'hospital' } },
    { korean: '의사', translations: { english: 'doctor' } },
    { korean: '환자', translations: { english: 'patient' } },
    { korean: '약', translations: { english: 'medicine' } },
    { korean: '증상', translations: { english: 'symptom' } }
];

const TranslationMatching = ({ moduleKey, moduleConfig, navigation, modulesOrder, moduleMap, level, chapter }) => {
    // Diagnostic logging moved into useEffect to avoid running on every render
    useEffect(() => {
        console.group('🎯 TranslationMatching: Component initializing');
        console.log('🔄 TranslationMatching component loaded');
        console.log('Props received:', {
            moduleKey,
            moduleConfig: !!moduleConfig,
            navigation: !!navigation,
            modulesOrder: modulesOrder?.length,
            moduleMap: Object.keys(moduleMap || {}).length,
            level,
            chapter
        });
        console.log('Navigation functions available:', {
            next: typeof navigation?.next,
            previous: typeof navigation?.previous,
            menu: typeof navigation?.menu
        });
        console.groupEnd();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleKey, level, chapter]); // Only re-log when key props change

    const { user } = useAuth();
    console.log('User from auth:', user);

    const sessionType = 'Learn';
    const moduleType = 'translation';

    const [gameState, setGameState] = useState('setup'); // setup, playing, completed
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [showResult, setShowResult] = useState(false);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const [gameConfig, setGameConfig] = useState({
        direction: 'korean_to_english',
        timeLimit: 30
    });
    const [timeLeft, setTimeLeft] = useState(30);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [exerciseNumber, setExerciseNumber] = useState(1);
    const [usedWords, setUsedWords] = useState(new Set());
    const [availableWords, setAvailableWords] = useState([]);
    const [vocabulary, setVocabulary] = useState(fallbackVocabularyData);
    const [_error, setError] = useState(null);
    const timerRef = useRef(null);

    // Sound effects helper
    const playSound = (type) => {
        const sounds = {
            click: '/sounds/click.mp3',
            correct: '/sounds/correct.mp3',
            incorrect: '/sounds/incorrect.mp3',
            complete: '/sounds/complete.mp3'
        };

        try {
            const audio = new Audio(sounds[type]);
            audio.volume = 0.5;
            audio.play().catch(_e => {
                // Silent fail if file doesn't exist
            });
        } catch (_e) {
            console.error('Error initializing audio:', _e);
        }
    };

    // Shuffle array function
    const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    // Data fetching with retry and safe fallback
    console.group('📚 TranslationMatching: Fetching vocabulary data');
    console.log('Fetching vocabulary for:', { level, chapter, institution: 'Korea_University' });

    const {
        data: fetchedVocabularyData,
        loading: vocabularyLoading,
        error: vocabularyError
    } = useVocabulary(level, chapter, 'Korea_University');

    console.log('📊 Vocabulary hook result:', {
        dataLength: fetchedVocabularyData?.length,
        loading: vocabularyLoading,
        error: vocabularyError?.message || vocabularyError
    });

    if (vocabularyError) {
        console.error('❌ Vocabulary fetch error:', vocabularyError);
    }

    console.groupEnd();

    useEffect(() => {
        console.group('🔄 TranslationMatching: Vocabulary useEffect triggered');
        console.log('Fetched data:', fetchedVocabularyData);
        console.log('Loading state:', vocabularyLoading);
        console.log('Error state:', vocabularyError);

        let mounted = true; // Flag to prevent state updates on unmounted component

        try {
            if (Array.isArray(fetchedVocabularyData) && fetchedVocabularyData.length > 0) {
                console.log('✅ Setting vocabulary from Firebase:', fetchedVocabularyData.length, 'words');
                console.log('Sample vocabulary items:', fetchedVocabularyData.slice(0, 3));
                if (mounted) setVocabulary(fetchedVocabularyData);
            } else if (vocabularyError) {
                console.warn('⚠️ Vocabulary fetch failed, using fallback:', vocabularyError);
                console.log('Fallback vocabulary:', fallbackVocabularyData.length, 'words');
                if (mounted) {
                    setVocabulary(fallbackVocabularyData);
                    setError(vocabularyError);
                }
            } else if (!vocabularyLoading) {
                console.log('📝 No vocabulary data and not loading, using fallback');
                if (mounted) setVocabulary(fallbackVocabularyData);
            } else {
                console.log('⏳ Still loading vocabulary data...');
            }
        } catch (err) {
            console.error('❌ Unexpected vocab error, using fallback:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                name: err.name
            });
            if (mounted) {
                setVocabulary(fallbackVocabularyData);
                setError(err);
            }
        }

        console.groupEnd();

        return () => {
            mounted = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [fetchedVocabularyData, vocabularyError, vocabularyLoading]);

    // Progress + modules order
    const { saveExerciseAttemptWithSession } = useSessionProgressActions(user?.uid, parseInt(level), parseInt(chapter), sessionType);

    // ---------------------------
    // Game Logic
    // ---------------------------

    // FIXED: Move endGame and loadNextQuestion declarations before handleSubmit to avoid TDZ
    const endGame = useCallback(async () => {
        playSound('complete');
        console.log('🏁 TranslationMatching: Ending game');
        setGameState('completed');
        setIsTimerActive(false);

        // Track module completion with new tracking system
        if (user?.uid) {
            const markModuleCompleted = async () => {
                try {
                    await TrackingV2Service.updateModuleProgress(
                        user.uid, level, chapter, 'translationMatching', {
                        completion: true,
                        completionTime: new Date().toISOString(),
                        lastQuestionCompleted: score.total,
                        totalQuestions: score.total,
                        questionsCorrect: score.correct,
                        accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0
                    }, {
                        moduleType: 'translationMatching',
                        completionMethod: 'user_finished',
                        finalScore: score,
                        accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0
                    }
                    );

                    console.log('✅ Module completion tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track module completion:', error);

                    // Fallback to old tracking system
                    ChapterProgress.updateModule(user.uid, level, chapter, 'Translation', {
                        completion: true,
                        completion_time: new Date().toISOString(),
                        last_question_completed: score.total
                    });
                }
            };

            markModuleCompleted();
        }
    }, [user?.uid, level, chapter, score]);

    const generateOptions = useCallback((correctWord, direction) => {
        const options = [correctWord.translations?.[direction === 'korean_to_english' ? 'english' : 'korean'] || correctWord.english || correctWord.korean];
        const others = vocabulary.filter(w => w.id !== correctWord.id);

        for (let i = 0; i < 3 && i < others.length; i++) {
            const randomWord = others[Math.floor(Math.random() * others.length)];
            const optionText = direction === 'korean_to_english'
                ? (randomWord.translations?.english || randomWord.english)
                : randomWord.korean;
            if (optionText && !options.includes(optionText)) {
                options.push(optionText);
            }
        }

        return shuffleArray(options);
    }, [vocabulary]);

    const loadNextQuestion = useCallback(async () => {
        console.log('🔄 TranslationMatching: Loading next question');
        if (!Array.isArray(vocabulary) || vocabulary.length === 0) return;

        const unused = availableWords.filter(word => !usedWords.has(word.korean));

        if (unused.length === 0) {
            if (score.total >= 10) {
                endGame();
                return;
            } else {
                setUsedWords(new Set());
                setAvailableWords([...vocabulary]);
                return;
            }
        }

        const randomWord = unused[Math.floor(Math.random() * unused.length)];
        setUsedWords(prev => new Set([...prev, randomWord.korean]));

        const options = generateOptions(randomWord, gameConfig.direction);

        const sourceLanguage = gameConfig.direction === 'korean_to_english' ? 'korean' : 'english';
        const targetLanguage = gameConfig.direction === 'korean_to_english' ? 'english' : 'korean';

        const sourceText =
            sourceLanguage === 'korean' ? randomWord.korean : (randomWord.translations?.[sourceLanguage] || randomWord.english || randomWord.meaning || randomWord.definition);
        const correctAnswer =
            targetLanguage === 'korean' ? randomWord.korean : (randomWord.translations?.[targetLanguage] || randomWord.english || randomWord.meaning || randomWord.definition);

        const question = { word: randomWord, sourceText, correctAnswer, options, direction: gameConfig.direction };

        setCurrentQuestion(question);
        setSelectedAnswer('');
        setShowResult(false);
        setTimeLeft(gameConfig.timeLimit);
        setIsTimerActive(true);

        // TRACKING: Mark vocabulary word as exposed when shown with new tracking system
        if (user?.uid && randomWord) {
            const wordId = randomWord.id || randomWord.korean;

            try {

                console.group('📊 TRACKING: Marking vocabulary as exposed');
                console.log('Word ID:', wordId);
                console.log('Korean:', randomWord.korean);
                console.log('English:', randomWord.translations?.english);

                await TrackingV2Service.markVocabularyExposed(
                    user.uid, level, chapter, wordId, 'translationMatching', {
                    exposureMethod: 'question_display',
                    exposedAt: new Date().toISOString(),
                    gameDirection: gameConfig.direction
                }
                );

                console.log('✅ Vocabulary exposure tracked');
                console.groupEnd();

            } catch (trackingError) {
                console.error('❌ Failed to track vocabulary exposure:', trackingError);

                // Fallback to old tracking system
                try {
                    await VocabularyTracking.markExposed(
                        user.uid,
                        parseInt(level),
                        parseInt(chapter),
                        sessionType,
                        wordId
                    );
                } catch (fallbackError) {
                    console.error('❌ Fallback tracking also failed:', fallbackError);
                }
                console.groupEnd();
            }
        }
    }, [vocabulary, availableWords, usedWords, gameConfig.direction, gameConfig.timeLimit, score.total, endGame, user, level, chapter, sessionType, generateOptions]);

    const handleSubmit = useCallback(async () => {
        console.group('🔄 TranslationMatching: handleSubmit called');
        console.log('Selected answer:', selectedAnswer);
        console.log('Show result:', showResult);
        console.log('Current question:', currentQuestion);

        if (!selectedAnswer || showResult) {
            console.log('❌ No answer selected or result already shown');
            console.groupEnd();
            return;
        }

        setIsTimerActive(false);
        const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
        if (isCorrect) {
            playSound('correct');
        } else {
            playSound('incorrect');
        }
        console.log('Answer is correct:', isCorrect);

        const newScore = {
            correct: score.correct + (isCorrect ? 1 : 0),
            total: score.total + 1
        };
        setScore(newScore);
        setShowResult(true);
        console.log('New score:', newScore);

        if (user?.uid && currentQuestion?.word) {
            const trackQuestionAttempt = async () => {
                try {
                    // FIXED: Use actual word ID instead of generic exercise number
                    const wordId = currentQuestion.word.id || currentQuestion.word.korean;

                    console.group('💾 TRACKING: Saving translation exercise attempt');
                    console.log('Word ID:', wordId);
                    console.log('Korean:', currentQuestion.word.korean);
                    console.log('English:', currentQuestion.word.translations?.english);
                    console.log('Exercise Number:', exerciseNumber);

                    // Add question attempt with new tracking system
                    await TrackingV2Service.addQuestionAttempt(
                        user.uid, level, chapter, 'translationMatching', exerciseNumber, {
                        questionType: 'translation_matching',
                        userResponse: selectedAnswer,
                        correctResponse: currentQuestion.correctAnswer,
                        isCorrect: isCorrect,
                        responseTime: gameConfig.timeLimit - timeLeft,
                        responseOptions: currentQuestion.options,
                        interactionType: 'multiple_choice',
                        gameDirection: gameConfig.direction,
                        sourceText: currentQuestion.sourceText,
                        wordId: wordId
                    }, {
                        questionType: 'translation_matching',
                        interactionType: 'multiple_choice',
                        gameDirection: gameConfig.direction,
                    }
                    );

                    // Update vocabulary mastery with new tracking system
                    await TrackingV2Service.updateVocabularyMastery(
                        user.uid, level, chapter, wordId, 'translationMatching', {
                        isCorrect: isCorrect,
                        userResponse: selectedAnswer,
                        correctResponse: currentQuestion.correctAnswer,
                        responseTime: gameConfig.timeLimit - timeLeft,
                        interactionType: 'multiple_choice',
                        gameDirection: gameConfig.direction,
                        sourceText: currentQuestion.sourceText
                    }, {
                        masteryType: 'translation_matching',
                        interactionType: 'multiple_choice',
                        gameDirection: gameConfig.direction
                    }
                    );

                    console.log('✅ Translation attempt tracked successfully');
                    console.groupEnd();

                } catch (error) {
                    console.error('❌ Failed to track translation attempt:', error);

                    // Fallback to old tracking system
                    try {
                        const wordId = currentQuestion.word.id || currentQuestion.word.korean;

                        // Save exercise attempt with word-specific ID
                        await saveExerciseAttemptWithSession({
                            exercise_id: `${wordId}_attempt${exerciseNumber}`,
                            word_id: wordId,
                            content_korean: currentQuestion.word.korean,
                            content_english: currentQuestion.word.translations?.english,
                            user_answer: selectedAnswer,
                            correct_answer: currentQuestion.correctAnswer,
                            is_correct: isCorrect,
                            first_attempt: true,
                            response_time_sec: gameConfig.timeLimit - timeLeft,
                            attempt_time: new Date().toISOString(),
                            category: 'Translation Practice',
                            subcategory: 'translation_matching',
                            module_type: moduleType,
                            exercise_number: exerciseNumber,
                            question_text: currentQuestion.sourceText,
                            options: currentQuestion.options,
                            translation_direction: gameConfig.direction
                        });

                        console.log('✅ Exercise attempt saved');

                        // TRACKING: Update vocabulary mastery with per-word attempt indexing
                        await VocabularyTracking.updateMastery(
                            user.uid,
                            parseInt(level),
                            parseInt(chapter),
                            sessionType,
                            wordId,
                            {
                                korean: currentQuestion.word.korean,
                                english: currentQuestion.word.translations?.english,
                                is_correct: isCorrect,
                                test_type: 'translation_matching',
                                translation_direction: gameConfig.direction,
                                response_time_sec: gameConfig.timeLimit - timeLeft
                            }
                        );

                        console.log('✅ Vocabulary mastery updated');

                        // NEW TRACKING: Add question to chapter progress with time_to_complete
                        const timeToComplete = gameConfig.timeLimit - timeLeft;
                        await ChapterProgress.addQuestion(
                            user.uid,
                            parseInt(level),
                            parseInt(chapter),
                            'Translation',
                            exerciseNumber,
                            {
                                options: currentQuestion.options,
                                correct_choice: currentQuestion.correctAnswer,
                                user_choice: selectedAnswer,
                                user_isCorrect: isCorrect,
                                time_to_complete: timeToComplete
                            }
                        );

                        console.log('✅ Question tracked in chapter progress');

                    } catch (fallbackError) {
                        console.error('❌ Fallback tracking also failed:', fallbackError);
                    }
                    console.groupEnd();
                }
            };

            // Execute tracking
            trackQuestionAttempt();
            setExerciseNumber(prev => prev + 1);
        }

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (newScore.total >= 10) {
                console.log('🏁 Game complete, ending game');
                endGame();
            } else {
                console.log('📝 Loading next question');
                loadNextQuestion();
            }
        }, 2000);

        console.groupEnd();
    }, [selectedAnswer, showResult, currentQuestion, score, user?.uid, saveExerciseAttemptWithSession, exerciseNumber, gameConfig.timeLimit, gameConfig.direction, timeLeft, moduleType, endGame, loadNextQuestion, level, chapter]);

    useEffect(() => {
        const handleTimeUp = () => {
            if (showResult) return;
            setSelectedAnswer('');
            handleSubmit();
        };

        if (isTimerActive && timeLeft > 0) {
            timerRef.current = setTimeout(() => {
                setTimeLeft(timeLeft - 1);
            }, 1000);
        } else if (timeLeft === 0 && gameState === 'playing') {
            handleTimeUp();
        }

        return () => clearTimeout(timerRef.current);
    }, [isTimerActive, timeLeft, gameState, showResult, handleSubmit]);

    const generateOptionsSafe = (correctWord, direction) => {
        try {
            const targetLanguage = direction === 'korean_to_english' ? 'english' : 'korean';
            const correctAnswer = targetLanguage === 'korean'
                ? correctWord.korean
                : (correctWord.translations?.[targetLanguage] || correctWord.english || correctWord.meaning || correctWord.definition);

            if (!correctAnswer) {
                console.error('❌ No correct answer found');
                return null;
            }

            const incorrectOptions = vocabulary
                .filter(word => {
                    try {
                        if (!word || !word.translations) return false;
                        const wordAnswer = targetLanguage === 'korean'
                            ? word.korean
                            : (word.translations?.[targetLanguage] || word.english || word.meaning || word.definition);
                        return wordAnswer && wordAnswer !== correctAnswer;
                    } catch (_e) {
                        return false;
                    }
                })
                .sort(() => Math.random() - 0.5)
                .slice(0, 3)
                .map(word => targetLanguage === 'korean'
                    ? word.korean
                    : (word.translations?.[targetLanguage] || word.english || word.meaning || word.definition));

            if (incorrectOptions.length < 3) {
                console.error('❌ Not enough incorrect options');
                return null;
            }

            return [correctAnswer, ...incorrectOptions].sort(() => Math.random() - 0.5);

        } catch (error) {
            console.error('❌ Error generating options:', error);
            return null;
        }
    };

    const loadFirstQuestionSafe = async (availableWordsList, usedWordsSet) => {
        safeLog('🎲 Loading first question', 'Starting', {
            available: availableWordsList?.length,
            used: usedWordsSet?.size
        });

        try {
            // Validate inputs
            if (!Array.isArray(availableWordsList) || availableWordsList.length === 0) {
                console.error('❌ Invalid availableWordsList');
                return;
            }

            // Filter unused words
            const unused = availableWordsList.filter(word => {
                try {
                    return word && word.korean && !usedWordsSet.has(word.korean);
                } catch (_e) {
                    console.error('❌ Error filtering word:', word, _e);
                    return false;
                }
            });

            safeLog(null, 'Filtered unused words', { count: unused.length });

            if (unused.length === 0) {
                console.error('❌ No unused words available');
                return;
            }

            // Select random word
            const randomWord = unused[Math.floor(Math.random() * unused.length)];

            if (!randomWord || !randomWord.korean || !randomWord.translations) {
                console.error('❌ Invalid random word selected:', randomWord);
                return;
            }

            safeLog(null, '✅ Selected word', { korean: randomWord.korean });

            // Update used words
            setUsedWords(prev => new Set([...prev, randomWord.korean]));

            // Generate options safely
            const options = generateOptionsSafe(randomWord, gameConfig.direction);

            if (!options || options.length < 4) {
                console.error('❌ Failed to generate options');
                return;
            }

            // Create question object
            const sourceLanguage = gameConfig.direction === 'korean_to_english' ? 'korean' : 'english';
            const targetLanguage = gameConfig.direction === 'korean_to_english' ? 'english' : 'korean';

            const sourceText = sourceLanguage === 'korean'
                ? randomWord.korean
                : (randomWord.translations?.[sourceLanguage] || randomWord.english || randomWord.meaning || randomWord.definition);
            const correctAnswer = targetLanguage === 'korean'
                ? randomWord.korean
                : (randomWord.translations?.[targetLanguage] || randomWord.english || randomWord.meaning || randomWord.definition);

            const question = {
                word: randomWord,
                sourceText,
                correctAnswer,
                options,
                direction: gameConfig.direction
            };

            safeLog(null, '✅ Question created', {
                source: sourceText,
                correct: correctAnswer
            });

            // Set all question state
            setCurrentQuestion(question);
            setSelectedAnswer('');
            setShowResult(false);
            setTimeLeft(gameConfig.timeLimit);
            setIsTimerActive(true);

            // TRACKING: Mark vocabulary word as exposed with new tracking system
            if (user?.uid && randomWord) {
                const wordId = randomWord.id || randomWord.korean;

                try {

                    console.group('📊 TRACKING: Marking first vocabulary as exposed');
                    console.log('Word ID:', wordId);

                    await TrackingV2Service.markVocabularyExposed(
                        user.uid, level, chapter, wordId, 'translationMatching', {
                        exposureMethod: 'first_question_display',
                        exposedAt: new Date().toISOString(),
                        gameDirection: gameConfig.direction
                    }
                    );

                    console.log('✅ First vocabulary exposure tracked');
                    console.groupEnd();

                } catch (trackingError) {
                    console.error('❌ Failed to track first vocabulary exposure:', trackingError);

                    // Fallback to old tracking system
                    try {
                        await VocabularyTracking.markExposed(
                            user.uid,
                            parseInt(level),
                            parseInt(chapter),
                            sessionType,
                            wordId
                        );
                    } catch (fallbackError) {
                        console.error('❌ Fallback tracking also failed:', fallbackError);
                    }
                    console.groupEnd();
                }
            }

            safeLog(null, '✅ Question loaded successfully', null);

        } catch (error) {
            console.error('❌ Error in loadFirstQuestionSafe:', error);
            console.error('Error stack:', error.stack);
            // Don't crash - just log and let user retry
        }
    };

    const startGame = () => {
        playSound('click');
        safeLog('🎮 TranslationMatching', 'Starting game', {
            vocabularyLength: vocabulary?.length,
            gameState
        });

        // CRITICAL: Validate vocabulary exists
        if (!vocabulary || !Array.isArray(vocabulary) || vocabulary.length === 0) {
            console.error('❌ Cannot start game: Invalid vocabulary', vocabulary);
            return;
        }

        // Track module start with new tracking system
        if (user?.uid) {
            const trackModuleStart = async () => {
                try {
                    await TrackingV2Service.updateModuleProgress(
                        user.uid, level, chapter, 'translationMatching', {
                        userStarted: true,
                        startDate: new Date().toISOString(),
                        lastOpened: new Date().toISOString(),
                        interactionType: 'multiple_choice',
                        totalWords: vocabulary.length,
                        gameDirection: gameConfig.direction,
                        timeLimit: gameConfig.timeLimit
                    }, {
                        moduleType: 'translationMatching',
                        interactionType: 'multiple_choice',
                        startMethod: 'user_initiated',
                        gameDirection: gameConfig.direction,
                        timeLimit: gameConfig.timeLimit,
                        vocabularyCount: vocabulary.length
                    }
                    );

                    console.log('✅ Module start tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track module start:', error);

                    // Fallback to old tracking system
                    ChapterProgress.updateModule(user.uid, level, chapter, 'Translation', {
                        user_started: true,
                        start_date: new Date().toISOString(),
                        last_opened: new Date().toISOString(),
                        interaction_type: 'Multiple Choice'
                    });
                }
            };

            trackModuleStart();
        }

        try {
            // Reset state
            setGameState('playing');
            setScore({ correct: 0, total: 0 });
            setShowResult(false);

            // Create fresh local copies
            const freshUsedWords = new Set();
            const freshAvailableWords = [...vocabulary];

            safeLog(null, '✅ Fresh state created', {
                availableCount: freshAvailableWords.length,
                usedCount: freshUsedWords.size
            });

            // Update state
            setUsedWords(freshUsedWords);
            setAvailableWords(freshAvailableWords);

            // Load first question with local data
            loadFirstQuestionSafe(freshAvailableWords, freshUsedWords);

            safeLog(null, '✅ Game started successfully', null);
        } catch (error) {
            console.error('❌ Error in startGame:', error);
            setGameState('setup'); // Rollback on error
        }
    };

    const _generateOptionsMemo = useCallback((correctWord, direction) => {
        const targetLanguage = direction === 'korean_to_english' ? 'english' : 'korean';
        const correctAnswer =
            targetLanguage === 'korean' ? correctWord.korean : correctWord.translations[targetLanguage];

        const incorrectOptions = vocabulary
            .filter(word => {
                const wordAnswer = targetLanguage === 'korean' ? word.korean : word.translations[targetLanguage];
                return wordAnswer !== correctAnswer;
            })
            .sort(() => Math.random() - 0.5)
            .slice(0, 3)
            .map(word => (targetLanguage === 'korean' ? word.korean : word.translations[targetLanguage]));

        return [correctAnswer, ...incorrectOptions].sort(() => Math.random() - 0.5);
    }, [vocabulary]);

    const handleAnswerSelect = (answer) => {
        playSound('click');
        console.log('🔄 TranslationMatching: Answer selected:', answer);
        if (!showResult) setSelectedAnswer(answer);
    };

    const resetGame = () => {
        playSound('click');
        console.log('🔄 TranslationMatching: Resetting game to setup');
        setGameState('setup');
        setCurrentQuestion(null);
        setSelectedAnswer('');
        setShowResult(false);
        setScore({ correct: 0, total: 0 });
        setIsTimerActive(false);
        setTimeLeft(30);
        setUsedWords(new Set());
        setAvailableWords([]);
        console.log('✅ Game reset complete');
    };

    const getTimerColor = () => {
        if (timeLeft > 20) return '#4CAF50';
        if (timeLeft > 10) return '#FF9800';
        return '#F44336';
    };

    // ---------------------------
    // UI Rendering
    // ---------------------------

    console.log('🎨 TranslationMatching render - current state:', {
        vocabularyLoading,
        vocabularyLength: vocabulary?.length,
        gameState,
        hasVocabulary: !!vocabulary,
        currentQuestion: !!currentQuestion,
        showResult,
        timeLeft,
        isTimerActive
    });

    // Only show loading if we genuinely have no data to show
    if (vocabularyLoading && (!vocabulary || vocabulary.length === 0)) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading vocabulary...</p>
            </div>
        );
    }

    if (!vocabulary || vocabulary.length === 0) {
        return (
            <div className="no-data-message">
                <h3>📚 No Vocabulary Available</h3>
                <p>Translation exercises for Level {level}, Chapter {chapter} are not available yet.</p>
                <button
                    onClick={() => {
                        playSound('click');
                        navigation?.next();
                    }}
                    className="action-btn primary"
                >
                    Continue
                </button>
            </div>
        );
    }

    // 🔧 DEBUG: Log render state
    console.log('🎨 TranslationMatching RENDER DEBUG:', {
        gameState,
        vocabularyLength: vocabulary?.length,
        vocabularyLoading,
        showSetup: gameState === 'setup',
        showPlaying: gameState === 'playing' && currentQuestion,
        showCompleted: gameState === 'completed'
    });

    return (
        <div className="translation-container">
            {/* Main Content - Header handled by App.js ChapterContainer */}
            <div className="feature-content">
                <div className="content-constraint">
                    {gameState === 'setup' && (
                        <div className="setup-screen">
                            <h2>Translation Matching</h2>
                            <p className="setup-description">
                                Match Korean words with their English translations
                            </p>

                            <div className="setup-config-container">
                                <div className="config-group">
                                    <label>
                                        Direction:
                                    </label>
                                    <select
                                        value={gameConfig.direction}
                                        onChange={(e) => {
                                            playSound('click');
                                            setGameConfig(prev => ({ ...prev, direction: e.target.value }));
                                        }}
                                        className="config-select"
                                    >
                                        <option value="korean_to_english">Korean → English</option>
                                        <option value="english_to_korean">English → Korean</option>
                                    </select>
                                </div>
                                <div className="config-group">
                                    <label>
                                        Time Limit:
                                    </label>
                                    <select
                                        value={gameConfig.timeLimit}
                                        onChange={(e) => {
                                            playSound('click');
                                            setGameConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }));
                                        }}
                                        className="config-select"
                                    >
                                        <option value={30}>30 seconds</option>
                                        <option value={60}>60 seconds</option>
                                        <option value={90}>90 seconds</option>
                                    </select>
                                </div>
                            </div>

                            <button
                                onClick={startGame}
                                className="action-btn primary start-btn"
                                style={{ marginTop: '4vh', fontSize: '1.2rem', padding: '1.5vh 3vw' }}
                            >
                                Start Game
                            </button>
                        </div>
                    )}

                    {gameState === 'playing' && currentQuestion && (
                        <div className="game-screen">
                            <div className="game-header">
                                <div className="score-display">
                                    Score: {score.correct}/{score.total}
                                </div>
                                <div className="timer-display" style={{ color: getTimerColor() }}>
                                    Time: {timeLeft}s
                                </div>
                            </div>

                            <div className="question-area">
                                <div className="question-header">
                                    <h3>
                                        Translate this word:
                                    </h3>
                                    <div className="source-word-large" style={{ fontSize: '3rem', margin: '3vh 0' }}>
                                        {currentQuestion.sourceText}
                                    </div>
                                </div>

                                <div className="options-grid">
                                    {currentQuestion.options.map((option, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleAnswerSelect(option)}
                                            disabled={showResult}
                                            className={`option-btn ${selectedAnswer === option ? 'selected' : ''
                                                } ${showResult && option === currentQuestion.correctAnswer ? 'correct' : ''
                                                } ${showResult && selectedAnswer === option && option !== currentQuestion.correctAnswer ? 'incorrect' : ''
                                                }`}
                                            style={{
                                                fontSize: '1.2rem',
                                                padding: '2vh',
                                                height: 'auto',
                                                minHeight: '8vh'
                                            }}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>

                                {showResult && (
                                    <div className={`result-message ${selectedAnswer === currentQuestion.correctAnswer ? 'success' : 'error'
                                        }`}
                                        style={{ marginTop: '2vh' }}>
                                        <div className="result-text">
                                            {selectedAnswer === currentQuestion.correctAnswer ? '✅ Correct!' : '❌ Incorrect!'}
                                        </div>
                                        <div className="correct-answer-text">
                                            Correct answer: {currentQuestion.correctAnswer}
                                        </div>
                                    </div>
                                )}

                                {!showResult && (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!selectedAnswer}
                                        className="action-btn primary submit-btn"
                                        style={{ marginTop: '4vh', width: '100%' }}
                                    >
                                        Submit Answer
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {gameState === 'completed' && (
                        <div className="completion-screen">
                            <h2>Game Complete!</h2>

                            <div className="final-results">
                                <div className="result-card">
                                    <h3>Final Score</h3>
                                    <div className="score-large">
                                        {score.correct}/{score.total}
                                    </div>
                                </div>

                                <div className="result-card">
                                    <h3>Accuracy</h3>
                                    <div className="accuracy-large">
                                        {Math.round((score.correct / score.total) * 100)}%
                                    </div>
                                </div>
                            </div>

                            <div className="completion-actions">
                                <button
                                    onClick={resetGame}
                                    className="action-btn secondary"
                                >
                                    Play Again
                                </button>
                                <button
                                    onClick={navigation?.next}
                                    className="action-btn primary"
                                >
                                    Next Module
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Controls - Only show when game is configured/setup or completed */}
                {gameState !== 'playing' && (
                    <div className="navigation-controls">
                        <button
                            onClick={navigation?.previous}
                            className="nav-btn secondary"
                            style={{ padding: '1vh 2vw' }}
                        >
                            <i className="fas fa-arrow-left"></i>
                            Previous
                        </button>
                        <button
                            onClick={navigation?.next}
                            className="nav-btn primary"
                            style={{ padding: '1vh 2vw' }}
                        >
                            Next
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>
                )}

                {/* 🔧 DEBUG: Fallback render to catch edge cases */}
                {!['setup', 'playing', 'completed'].includes(gameState) && (
                    <div className="error-message">
                        <h3>🔧 DEBUG: Unknown Game State</h3>
                        <p>Current state: <code>{gameState}</code></p>
                        <p>Vocabulary: {vocabulary?.length || 0} words</p>
                        <p>Loading: {vocabularyLoading ? 'Yes' : 'No'}</p>
                        <button onClick={() => setGameState('setup')} className="action-btn primary">
                            Reset to Setup
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TranslationMatching;
